import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api';

/** Only the part of the profile this hook is responsible for. */
type MasterProfile = { avatarUrl: string | null };

export const masterProfileKey = ['master', 'profile'] as const;

/**
 * The master's own photo.
 *
 * Scoped deliberately to the avatar: the rest of the settings form is still
 * local state, and a hook that fetched the whole profile would imply the form
 * was wired when it is not.
 */
export const useMasterAvatar = () => {
  const queryClient = useQueryClient();

  const profile = useQuery({
    queryKey: masterProfileKey,
    queryFn: () => api.get<MasterProfile>('/master/profile'),
  });

  const write = (data: MasterProfile) => queryClient.setQueryData(masterProfileKey, data);

  const upload = useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('photo', file);

      return api.post<MasterProfile>('/master/profile/avatar', body);
    },
    onSuccess: write,
  });

  const remove = useMutation({
    mutationFn: () => api.delete<MasterProfile>('/master/profile/avatar'),
    onSuccess: write,
  });

  return {
    avatarUrl: profile.data?.avatarUrl ?? null,
    isPending: profile.isPending,
    upload: (file: File) => upload.mutate(file),
    remove: () => remove.mutate(),
    isSaving: upload.isPending || remove.isPending,
    failed: upload.isError || remove.isError,
    error: upload.error ?? remove.error,
  };
};
